import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Scroll restoration for an internally-scrolling container (not the window).
 *
 * React Router's built-in `<ScrollRestoration />` only saves/restores `window` scroll, but in
 * this app the document height is pinned to the viewport (html/body/#root are `height: 100%`,
 * see `src/index.css`) and content scrolls inside `overflow: auto` containers
 * instead — so the window-based component is a no-op. This hook restores the scroll offset of a
 * specific container.
 *
 * Usage — attach the returned callback ref to the scrolling element:
 *
 *   const scrollRef = useScrollRestoration('timecards-list');
 *   return <TableContainer ref={scrollRef}>…</TableContainer>;
 *
 * Behaviour mirrors the browser default:
 *   - PUSH (forward navigation to a new page) → start at the top.
 *   - POP (back / forward buttons) / REPLACE → restore the offset saved for that URL.
 *
 * Why URL-keyed (pathname+search), not `location.key`: list pages normalise their own URL on
 * mount via `setSearchParams(..., { replace: true })`, which mints a fresh `location.key` and a
 * REPLACE navigation right after the POP that brought us back. Keying by `location.key` would
 * miss the saved entry, and re-running on every key change would let that REPLACE cancel the
 * in-flight restore. Keying by URL is stable across that replace, and the effect only re-runs on
 * pathname changes so the replace can't interrupt it. (Same URL → same remembered scroll, which
 * is the desired behaviour anyway.)
 *
 * The container often mounts empty and fills in asynchronously (e.g. a list rendering
 * cached/fetched rows) after navigating back, so it isn't tall enough to reach the saved offset
 * right away. Restoration waits across animation frames until the content grows enough or a
 * short budget elapses.
 *
 * Positions live for the SPA session and intentionally do not survive a full page reload, which
 * matches React Router's own default. `scopeKey` namespaces entries so independent containers
 * sharing one URL don't clobber each other.
 */

const SETTLE_TIMEOUT_MS = 2000;

// Soft cap so a long-lived session doesn't grow the map without bound. Map preserves insertion
// order, so the first key is the oldest.
const MAX_ENTRIES = 200;

const positions = new Map<string, number>();

const savePosition = (storageKey: string, top: number): void => {
    // Re-insert so the most recently touched key moves to the end, keeping the trim roughly LRU.
    positions.delete(storageKey);
    positions.set(storageKey, top);
    if (positions.size > MAX_ENTRIES) {
        const oldest = positions.keys().next().value;
        if (oldest !== undefined) {
            positions.delete(oldest);
        }
    }
};

export const useScrollRestoration = (scopeKey = ''): ((node: HTMLElement | null) => void) => {
    const { pathname, search } = useLocation();
    const navigationType = useNavigationType();

    const storageKey = `${scopeKey}::${pathname}${search}`;

    // Latest storageKey for the scroll listener (a stable callback that can't close over the
    // current value). Updated in an effect, never during render. Safe because the listener only
    // fires on user scroll — well after this effect has run for the active URL.
    const storageKeyRef = useRef(storageKey);
    useEffect(() => {
        storageKeyRef.current = storageKey;
    }, [storageKey]);

    const nodeRef = useRef<HTMLElement | null>(null);
    // Suppress recording while we programmatically restore (the container may still be clamping to
    // a too-short height, which would otherwise overwrite the saved offset with a smaller value).
    const isRestoringRef = useRef(false);

    const handleScroll = useCallback(() => {
        const el = nodeRef.current;
        if (!el || isRestoringRef.current) {
            return;
        }
        savePosition(storageKeyRef.current, el.scrollTop);
    }, []);

    // Callback ref: (un)subscribe the scroll listener as the element attaches/detaches. Stable
    // identity so it doesn't re-fire on every render.
    const setRef = useCallback(
        (node: HTMLElement | null) => {
            if (nodeRef.current) {
                nodeRef.current.removeEventListener('scroll', handleScroll);
            }
            nodeRef.current = node;
            if (node) {
                node.addEventListener('scroll', handleScroll, { passive: true });
            }
        },
        [handleScroll]
    );

    // Decide once per pathname change: reset to top on a forward PUSH, otherwise restore.
    // `navigationType` and `storageKey` are read directly (closure captures the values from the
    // render in which `pathname` changed) but deliberately kept OUT of the dependency array — a
    // same-pathname REPLACE (e.g. a list normalising its own query string on mount) must not
    // re-run this effect and cancel an in-flight restore.
    useLayoutEffect(() => {
        const isForward = navigationType === 'PUSH';
        const target = isForward ? 0 : (positions.get(storageKey) ?? 0);

        if (target === 0) {
            // Start at the top. A freshly mounted container is already at 0; reset an existing one
            // in case it persisted across the navigation. No need to wait for height.
            const el = nodeRef.current;
            if (el) {
                el.scrollTop = 0;
            }
            return;
        }

        isRestoringRef.current = true;
        const start = performance.now();
        let raf = 0;

        const setScrollTopInstant = (el: HTMLElement, top: number) => {
            const previousScrollBehavior = el.style.scrollBehavior;
            el.style.scrollBehavior = 'auto'; // jump instantly, don't animate
            el.scrollTop = top;
            el.style.scrollBehavior = previousScrollBehavior;
        };

        const apply = () => {
            const el = nodeRef.current;
            // Wait until the element exists and is tall enough to actually reach `target`.
            if (el && el.scrollHeight - el.clientHeight >= target) {
                setScrollTopInstant(el, target);
                isRestoringRef.current = false;
                return;
            }
            if (performance.now() - start < SETTLE_TIMEOUT_MS) {
                raf = requestAnimationFrame(apply);
                return;
            }
            // Budget exhausted — apply whatever the container can take and stop.
            if (el) {
                setScrollTopInstant(el, target);
            }
            isRestoringRef.current = false;
        };

        raf = requestAnimationFrame(apply);

        return () => {
            cancelAnimationFrame(raf);
            isRestoringRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on pathname only by design (see comment above)
    }, [pathname, scopeKey]);

    return setRef;
};
